import { useEffect, useRef, useState } from 'react';

/**
 * Karaoke / streaming reveal driven by the real WS alignment, replacing the
 * design's local timers (`SpokenBody`'s STREAM + SPEECH clocks).
 *
 * Mirrors the proven algorithm of `apps/client`'s `KaraokeText`:
 *   - `getRenderEndIdx()` → how many chars of `text` to show now (synced to the
 *     audio, ~250ms ahead). `getCharIndex()` → index of the char being spoken,
 *     or -1. Both are GETTERS that don't trigger re-renders — we poll them in a
 *     single `requestAnimationFrame` loop.
 *   - **`renderEnd` is monotonically increasing within a turn**: once a char is
 *     revealed it stays revealed, even if the alignment momentarily reports a
 *     smaller value. Without this, the live bubble shrinks/disappears mid-stream
 *     ("aparece y desaparece") when the getter retrocede un frame.
 *   - Reset to 0 only when `text` goes back to empty (a new turn recycles the
 *     bubble) or when `active` turns false (closed history bubble → show full).
 *
 * `active` gates the loop. Lo piden dos casos: la burbuja VIVA del coach
 * (revelado + resaltado sincronizados al audio del WS) y una burbuja CERRADA
 * que se está volviendo a escuchar (sólo resaltado, contra el reloj del
 * `<audio>` del replay). Quién es quién lo dice `getRenderEndIdx`: si devuelve
 * `Infinity` el texto ya está entero y sólo corre el karaoke. Las burbujas
 * cerradas en silencio no corren el loop.
 */
export interface KaraokeState {
  /** Chars of the text to reveal (`text.slice(0, renderEnd)`). */
  renderEnd: number;
  /** Char index currently spoken, or -1 when idle. */
  speakingChar: number;
}

export function useKaraoke(
  active: boolean,
  text: string,
  getRenderEndIdx: () => number,
  getCharIndex: () => number,
): KaraokeState {
  // Quién arranca oculto lo decide el GETTER, no el flag: la burbuja viva pide
  // revelado progresivo (getter finito → arranca en 0), mientras que una
  // burbuja cerrada que se re-escucha ya tiene el texto entero (getter
  // `Infinity` → arranca revelada). Atarlo a `active` hacía que el primer
  // render de un replay mostrara la burbuja VACÍA.
  const [renderEnd, setRenderEnd] = useState<number>(() =>
    active && Number.isFinite(getRenderEndIdx()) ? 0 : Infinity,
  );
  const [speakingChar, setSpeakingChar] = useState<number>(-1);
  const lastEndRef = useRef<number>(renderEnd);
  const lastCharRef = useRef<number>(-1);
  // Mirror `text` in a ref so the rAF loop reads the latest version without
  // re-subscribing on every token (that re-subscribe is what reset renderEnd
  // to 0 each token and caused the flicker).
  const textRef = useRef<string>(text);
  textRef.current = text;

  useEffect(() => {
    if (!active) {
      // Closed turn: reveal everything and clear the highlight.
      lastEndRef.current = Infinity;
      lastCharRef.current = -1;
      setRenderEnd(Infinity);
      setSpeakingChar(-1);
      return undefined;
    }

    let raf = 0;
    const tick = (): void => {
      const t = textRef.current;
      // New turn (text emptied): reset and wait for the next token.
      if (t.length === 0) {
        if (lastEndRef.current !== 0) {
          lastEndRef.current = 0;
          setRenderEnd(0);
        }
        if (lastCharRef.current !== -1) {
          lastCharRef.current = -1;
          setSpeakingChar(-1);
        }
        raf = requestAnimationFrame(tick);
        return;
      }

      const rawChar = getCharIndex();
      const clampedChar = rawChar >= t.length ? t.length - 1 : rawChar;
      if (clampedChar !== lastCharRef.current) {
        lastCharRef.current = clampedChar;
        setSpeakingChar(clampedChar);
      }

      const target = getRenderEndIdx();
      const desired = !Number.isFinite(target)
        ? t.length
        : Math.min(t.length, Math.max(0, Math.floor(target)));
      // Monotonically increasing — never shrink the revealed text mid-turn.
      if (desired > lastEndRef.current) {
        lastEndRef.current = desired;
        setRenderEnd(desired);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, getRenderEndIdx, getCharIndex]);

  return { renderEnd, speakingChar };
}
